import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import {
    Box,
    Container,
    Flex,
    Grid,
    GridItem,
    Icon,
    IconButton,
    Image,
    Skeleton,
    Stack,
    Text,
    useBreakpointValue,
} from "@chakra-ui/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@phosphor-icons/react/dist/ssr";
import Head from "next/head";
import { useEffect, useState } from "react";
import { useRef } from "react";
import { Autoplay, EffectCoverflow, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import GuiaCardSecondHomes from "@/components/secondhomes/GuiaCardSecondHomes";
import { useGetDevelopmentsQuery } from "@/hooks/microservicesApi";
import useFormatter from "@/hooks/useFormatter";
import Footer from "@/sections/home/Footer";
import Header from "@/sections/home/Header";
import { wrapBgImage } from "@/utilities/helpers/wrapBgImage";

export default function SecondHomes() {
    const body = {
        company: ["GUIA", "GUIACDMX", "GUIAQRO", "GUIACABOS"],
        page: 0,
        limit: 100,
        labels: ["SECONDHOMES"],
    };
    const { data, isLoading, isFetching } = useGetDevelopmentsQuery({ body: body });
    const { NumberF } = useFormatter();
    const [dataOrderRandom, setDataOrderRandom] = useState([]);
    const [dataFilter, setDataFilter] = useState([]);
    const [cities, setCities] = useState([]);
    const [city, setCity] = useState("Todas");
    const swiperRef = useRef(null);

    const isGreaterThanMd = useBreakpointValue({ base: false, md: true });
    const isGreaterThanLg = useBreakpointValue({ base: false, lg: true });

    const getSlidesPerView = () => {
        if (isGreaterThanLg && dataFilter?.length >= 3) {
            return 3;
        } else if (isGreaterThanMd && dataFilter?.length >= 2) {
            return 2;
        } else {
            return 1;
        }
    };

    useEffect(() => {
        if (data) {
            let numero = 0;
            let result = data.map((item) => {
                numero = Math.floor(Math.random() * (1 - (1000 - 1))) + 1000;
                return {
                    ...item,
                    idRandom: numero,
                };
            });

            let cts = ["Todas"];
            result.map((dev) => {
                if (dev.location_props) {
                    if (dev.location_props.city) {
                        cts.push(dev.location_props.city);
                    }
                }
            });
            cts = [...new Set(cts)];
            setCities(cts);
            setDataOrderRandom(result);
            setDataFilter(result);
        }
    }, [data]);

    useEffect(() => {
        if (city) {
            if (city !== "Todas") {
                const result = data?.filter((dev) => dev.location_props?.city === city);
                setDataFilter(city !== "x" ? result : data);
            } else {
                setDataFilter(dataOrderRandom);
            }
        }
    }, [city]);

    const showSkeleton = () => {
        return (
            <Flex
                padding="30px 50px"
                w="100%"
                h="440px"
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap="2"
            >
                <Skeleton w="25%" h="90%" borderRadius="10px" />
                <Skeleton w="40%" h="100%" borderRadius="10px" />
                <Skeleton w="25%" h="90%" borderRadius="10px" />
            </Flex>
        );
    };

    const bgImage =
        "https://media.grupoguia.mx/assets/home-page/secondhomes/fondo-secondhomes.webp";

    return (
        <>
            <Head>
                <title>Second Homes | Grupo Guía</title>
                <link rel="canonical" href="https://grupoguia.mx/secondhomes" />
                <link rel="alternate" href="https://grupoguia.mx/secondhomes" hrefLang="es-mx" />
            </Head>
            <Header />
            <Container maxW="8xl" {...(!isGreaterThanMd && { paddingInline: "0" })}>
                <Box {...(!isGreaterThanMd && { full: "full" })} mb={isGreaterThanMd ? "14" : "8"}>
                    <Box w="full" mx="auto" maxW="3x1" paddingInline="0">
                        <Flex
                            w="100vw"
                            h="75vh"
                            id="fondo"
                            backgroundImage={wrapBgImage(bgImage)}
                            backgroundSize="cover"
                            backgroundPosition="center center"
                            backgroundRepeat="no-repeat"
                            position="relative"
                            p="0"
                            bgColor="gray.100"
                            borderRadius="20px"
                            overflow="hidden"
                        >
                            <Flex
                                direction="column"
                                w="100%"
                                position="absolute"
                                left="0"
                                right="0"
                                height="100%"
                                paddingBottom={16}
                                justifyContent="end"
                                alignItems="center"
                                color="white"
                                bg="#121212AB"
                                textAlign="center"
                            >
                                <Image
                                    height="70px"
                                    src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-secondhomes.svg"
                                    alt="Icon Second Homes"
                                    mb={6}
                                />
                                <Text fontWeight="700" fontSize={isGreaterThanMd ? "4xl" : "xl"}>
                                    Una nueva oportunidad de inversión
                                </Text>
                                <Text fontWeight={400} fontSize={isGreaterThanMd ? "2xl" : "lg"}>
                                    en los destinos más exclusivos de México.
                                </Text>
                            </Flex>
                        </Flex>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    pt={6}
                    gap={6}
                >
                    <Flex justifyContent="center" pb="1rem">
                        <Text fontSize="lg" fontWeight={400} color="black">
                            Encuentra el lugar para ti...
                        </Text>
                    </Flex>

                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        gap={6}
                        color="#4B4B4B"
                        wrap="wrap"
                    >
                        <Text fontWeight="600" fontSize="lg">
                            Cancún
                        </Text>
                        <Image
                            boxSize="8px"
                            src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-cube.svg"
                            alt="Icon"
                        />
                        <Text fontWeight="600" fontSize="lg">
                            Puerto Vallarta
                        </Text>
                        <Image
                            boxSize="8px"
                            src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-cube.svg"
                            alt="Icon"
                            display={isGreaterThanMd ? "flex" : "none"}
                        />
                        <Text fontWeight="600" fontSize="lg">
                            Tapalpa
                        </Text>
                        <Image
                            boxSize="8px"
                            src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-cube.svg"
                            alt="Icon"
                        />
                        <Text fontWeight="600" fontSize="lg">
                            Manzanillo
                        </Text>
                        <Image
                            boxSize="8px"
                            src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-cube.svg"
                            alt="Icon"
                        />
                        <Text fontWeight="600" fontSize="lg">
                            Los Cabos
                        </Text>
                    </Flex>
                </Box>

                <Box my={6}>
                    <Grid
                        templateColumns={{
                            base: "repeat(1, 1fr)",
                            md: "repeat(2, 1fr)",
                            lg: "repeat(4, 1fr)",
                        }}
                        // {isGreaterThanMd ? "repeat(4, 1fr)" : "repeat(1, 1fr)"}
                        gap={4}
                        alignItems="center"
                        mt={2}
                        pt={6}
                        justifyContent="center"
                        maxW="4xl"
                        margin="auto"
                        textAlign="center"
                    >
                        <GridItem colSpan={1}>
                            <Flex
                                alignItems="center"
                                width="14rem"
                                height="16rem"
                                justifyContent="end"
                                textAlign="center"
                                flexDirection="column"
                                borderRadius="10px"
                                px={2}
                                py={4}
                                margin="auto"
                                boxShadow="rgb(221, 221, 221) 0px 4px 8px 0px"
                                paddingBottom="3rem"
                            >
                                <Image
                                    boxSize="70px"
                                    src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-1.svg"
                                    alt="Icon"
                                    margin="1rem"
                                />
                                <Text
                                    fontSize="md"
                                    fontWeight={400}
                                    lineClamp="3"
                                    color="black"
                                    lineHeight="1"
                                >
                                    Propiedades con entrega inmediata
                                </Text>
                            </Flex>
                        </GridItem>
                        <GridItem colSpan={1}>
                            <Flex
                                alignItems="center"
                                width="14rem"
                                height="16rem"
                                justifyContent="end"
                                textAlign="center"
                                flexDirection="column"
                                borderRadius="10px"
                                px={2}
                                py={4}
                                margin="auto"
                                boxShadow="rgb(221, 221, 221) 0px 4px 8px 0px"
                            >
                                <Image
                                    boxSize="70px"
                                    src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-2.svg"
                                    alt="Icon"
                                    margin="1rem"
                                />
                                <Text fontSize="md" fontWeight={400} lineClamp={3} color="black">
                                    Grupo Guia te orienta y acompaña durante todo el proceso de
                                    compra.
                                </Text>
                            </Flex>
                        </GridItem>
                        <GridItem colSpan={1}>
                            <Flex
                                alignItems="center"
                                width="14rem"
                                height="16rem"
                                justifyContent="end"
                                textAlign="center"
                                flexDirection="column"
                                borderRadius="10px"
                                px={2}
                                py={4}
                                margin="auto"
                                boxShadow="rgb(221, 221, 221) 0px 4px 8px 0px"
                                paddingBottom="1rem"
                            >
                                <Image
                                    boxSize="70px"
                                    src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-3.svg"
                                    alt="Icon"
                                    margin="1rem"
                                />
                                <Text fontSize="md" fontWeight={400} lineClamp="3" color="black">
                                    Preventas exclusivas con los mejores precios del mercado
                                </Text>
                            </Flex>
                        </GridItem>
                        <GridItem colSpan={1}>
                            <Flex
                                alignItems="center"
                                width="14rem"
                                height="16rem"
                                justifyContent="end"
                                textAlign="center"
                                flexDirection="column"
                                borderRadius="10px"
                                px={2}
                                py={4}
                                margin="auto"
                                boxShadow="rgb(221, 221, 221) 0px 4px 8px 0px"
                            >
                                <Image
                                    boxSize="70px"
                                    src="https://media.grupoguia.mx/assets/home-page/secondhomes/icons/icon-4.svg"
                                    alt="Icon"
                                    margin="1rem"
                                />
                                <Text fontSize="md" fontWeight={400} lineClamp={3} color="black">
                                    Solo proyectos de desarrolladores certificados
                                </Text>
                            </Flex>
                        </GridItem>
                    </Grid>
                </Box>
                <Box my={6} py={10}>
                    <Stack>
                        {cities ? (
                            <Grid
                                templateColumns={
                                    isGreaterThanMd
                                        ? cities.length < 4
                                            ? "repeat(" + cities.length + ", 1fr)"
                                            : "repeat(4, 1fr)"
                                        : "repeat(1, 1fr)"
                                }
                                gap={4}
                                alignItems="center"
                                mt={2}
                                pt={6}
                                justifyContent="center"
                                maxW="3xl"
                                margin="auto"
                                textAlign="center"
                                borderBottomWidth="2px"
                                borderBottomColor="black"
                            >
                                {cities.map((ct, idx) => (
                                    <GridItem
                                        key={`cities-grid-item-${idx}`}
                                        colSpan={1}
                                        textAlign={
                                            idx === 0
                                                ? isGreaterThanMd
                                                    ? "left"
                                                    : "center"
                                                : idx === cities.length - 1
                                                  ? "end"
                                                  : "center"
                                        }
                                        w="100%"
                                        pb={6}
                                    >
                                        <Text
                                            marginRight={
                                                idx === 0
                                                    ? isGreaterThanMd
                                                        ? "8rem"
                                                        : "4rem"
                                                    : idx === cities.length - 1
                                                      ? isGreaterThanMd
                                                          ? "0px"
                                                          : "4rem"
                                                      : "4rem"
                                            }
                                            marginLeft={
                                                idx === 0
                                                    ? isGreaterThanMd
                                                        ? "0rem"
                                                        : "4rem"
                                                    : idx === cities.length - 1
                                                      ? isGreaterThanMd
                                                          ? "8rem"
                                                          : "4rem"
                                                      : "4rem"
                                            }
                                            fontWeight={city === ct ? "bold" : "400"}
                                            fontSize="sm"
                                            pb={city === ct ? 4 : 6}
                                            cursor="pointer"
                                            textAlign="center"
                                            color="black"
                                            _hover={{ color: "gray.300" }}
                                            onClick={() => {
                                                setCity(ct);
                                            }}
                                            borderBottomWidth={city === ct ? "5px" : "0px"}
                                            borderBottomColor="black"
                                        >
                                            {ct}
                                        </Text>
                                    </GridItem>
                                ))}
                            </Grid>
                        ) : (
                            <></>
                        )}
                    </Stack>
                    <Container maxW="full" mt={10} mx="auto">
                        {(isLoading || isFetching) && showSkeleton()}
                        <Swiper
                            ref={swiperRef}
                            loop
                            modules={[Pagination, Autoplay, EffectCoverflow]}
                            effect="coverflow"
                            coverflowEffect={{
                                rotate: 0,
                                stretch: 0,
                                depth: 80,
                                modifier: 1,
                                scale: 0.9,
                                slideShadows: false,
                            }}
                            pagination={{
                                clickable: true,
                                renderBullet: (index, className) =>
                                    `<span class="${className} custom-bullet"></span>`,
                            }}
                            spaceBetween={10}
                            slidesPerView={getSlidesPerView()}
                            autoplay={{
                                delay: 5000,
                                disableOnInteraction: false,
                            }}
                            navigation={false}
                            centeredSlides={true}
                            centeredSlidesBounds={true}
                        >
                            {dataFilter?.map((item, index) => (
                                <SwiperSlide
                                    key={item.wiggot_code}
                                    style={{
                                        height: "470px",
                                        justifyItems: "center",
                                    }}
                                >
                                    <GuiaCardSecondHomes
                                        key={item.wiggot_code}
                                        id={item.wiggot_code}
                                        title={item.name}
                                        subTitle={`Desde ${NumberF(item.min_presale || item.min_sale || item.min_lease, true, "", 2)}`}
                                        imageUrl={
                                            item.photo.includes("http")
                                                ? item.photo
                                                : `https://media.wiggot.mx/${item.photo}-l.jpg`
                                        }
                                        link={`/desarrollo/${item.url_name}`}
                                        buttonText="Ver más"
                                        referred={true}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        {dataFilter.length > 1 && (
                            <>
                                {/* Botón Anterior */}
                                <IconButton
                                    onClick={() => swiperRef.current.swiper.slidePrev()}
                                    aria-label="Previous slide"
                                    position="absolute"
                                    top="50%"
                                    left="20px"
                                    transform="translateY(-50%)"
                                    background="white"
                                    borderRadius="full"
                                    boxShadow="md"
                                    _hover={{ background: "gray.300" }}
                                    zIndex={1}
                                    disabled={
                                        dataFilter?.length < 3 && isGreaterThanMd ? true : false
                                    }
                                >
                                    <Icon boxSize={5} color="brand.500">
                                        <ArrowLeftIcon />
                                    </Icon>
                                </IconButton>
                                {/* Botón Siguiente */}
                                <IconButton
                                    onClick={() => swiperRef.current.swiper.slideNext()}
                                    aria-label="Next slide"
                                    position="absolute"
                                    top="50%"
                                    right="20px"
                                    transform="translateY(-50%)"
                                    background="white"
                                    borderRadius="full"
                                    boxShadow="md"
                                    _hover={{ background: "gray.300" }}
                                    zIndex={1}
                                    disabled={
                                        dataFilter?.length < 3 && isGreaterThanMd ? true : false
                                    }
                                >
                                    <Icon boxSize={5} color="brand.500">
                                        <ArrowRightIcon />
                                    </Icon>
                                </IconButton>
                            </>
                        )}
                    </Container>
                </Box>
                <Box>
                    <Footer esGuiaRent={false} />
                </Box>
            </Container>
        </>
    );
}
